import { useEffect, useRef, useState } from 'react';

import { IbackofficeUserData } from 'types/configuration.types';
import { IQuoteResponsibleUser } from 'types/quote.types';
import BordPhoto from 'components/NudosComponents/BordPhoto/BordPhoto';
import { useQuoteResponsible } from 'views/Quote/hooks/useQuoteResponsible';
import { BordButton, BordSearchBar } from 'bord-design-system';
import { useClickOutside } from 'hooks/useClickOutside';
import { HoverReveal } from 'components/HoverReveal/HoverReveal';

import './QuoteResponsibles.scss';

enum EQuoteResponsiblesActions {
  ASSIGN = 'assign',
  UNASSIGN = 'unassign'
}

interface IQuoteResponsibles {
  responsibleUsers?: { id: number; quoteId: number; userId: number }[];
  quoteId: number;
  users?: IbackofficeUserData[];
  refreshData?: () => void;
}

const NUMBER_OF_USERS_TO_DISPLAY = 3;

export const QuoteResponsibles = ({ users, quoteId, responsibleUsers = [], refreshData }: IQuoteResponsibles) => {
  const [style, setStyle] = useState({});
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [responsibles, setResponsibles] = useState<IQuoteResponsibleUser[]>(() => responsibleUsers);
  const [loadingUserId, setLoadingUserId] = useState<number | undefined>();
  const ref = useRef<HTMLDivElement>(null);

  const userIdsSelected = responsibles?.map(data => data?.userId) ?? [];

  const totalUsersToDisplay = responsibles?.length - NUMBER_OF_USERS_TO_DISPLAY;
  const numberOfUsersDosentDisplayed = totalUsersToDisplay <= 0 ? 0 : totalUsersToDisplay;

  const { assignResponsible, unassignResponsible, getResponsibles } = useQuoteResponsible();

  useClickOutside(ref, () => setOpen(false));

  const handleOpen = (evt: React.MouseEvent<HTMLButtonElement>) => {
    const rect = evt.currentTarget.getBoundingClientRect();

    if (!rect) return;
    setStyle({
      position: 'absolute',
      top: rect.bottom + window.scrollY + 10,
      left: rect.right + window.scrollX - 380
    });

    setOpen(!open);
  };

  /** Refresca los responsables vigentes de la cotización desde el backend. */
  const refreshResponsibles = async () => {
    const fresh = await getResponsibles(quoteId);
    setResponsibles(fresh ?? []);
  };

  /**
   * Asigna o desasigna un responsable y, tras el éxito, refresca la data
   * autoritativa de la cotización y las opciones de filtro del listado.
   */
  const handleClick = (userId: number, action: EQuoteResponsiblesActions) => async () => {
    setLoadingUserId(userId);
    try {
      if (action === EQuoteResponsiblesActions.ASSIGN) {
        await assignResponsible(quoteId, userId);
      }
      if (action === EQuoteResponsiblesActions.UNASSIGN) {
        const id = responsibles?.find(data => data?.userId === userId)?.id;
        if (!id) return;
        await unassignResponsible(id);
      }
      await refreshResponsibles();
      refreshData?.();
    } catch {
      // el error ya fue notificado al usuario desde el hook
    } finally {
      setLoadingUserId(undefined);
    }
  };

  const search = (user: IbackofficeUserData) => {
    if (!searchText) return true;
    return (
      user.email?.includes(searchText) || user.firstName?.includes(searchText) || user.lastName?.includes(searchText)
    );
  };

  const renderUsers = (id: number) => {
    const user = users?.find(user => user?.id === id);
    return (
      <div key={id} className="userItem">
        <div className="avatar">
          <BordPhoto
            key={id}
            url={user?.photoProfile}
            variant="circle"
            size="s24"
            avatarProps={{
              className: 'avatarIcon',
              variant: 'circularUser',
              standardSize: 48
            }}
          />
        </div>
        <div className="userTextContent">
          <div className="userName" title={user?.firstName}>
            {user?.firstName}
          </div>
          <div className="userEmail" title={user?.email}>
            {user?.email}
          </div>
        </div>
      </div>
    );
  };

  const renderAvatarGroup = (id: number) => {
    const user = users?.find(user => user?.id === id);
    return (
      <div key={id} className="avatar">
        <BordPhoto
          key={id}
          url={user?.photoProfile}
          variant="circle"
          size="s24"
          avatarProps={{
            variant: 'circularUser',
            standardSize: 48
          }}
        />
      </div>
    );
  };

  const renderUserItem = (user: IbackofficeUserData) => {
    const isAssigned = userIdsSelected?.includes(user?.id);
    return (
      <div key={user?.id} className="userListItem">
        <div className="userInfo">
          <div className="avatar">
            <BordPhoto
              url={user?.photoProfile}
              variant="circle"
              size="s24"
              avatarProps={{
                variant: 'circularUser'
              }}
            />
          </div>
          <div className="userDetails">
            <div className="userName">{user?.firstName}</div>
            <div className="userEmail">{user?.email}</div>
          </div>
        </div>

        <BordButton
          label={isAssigned ? 'Desasignar' : 'Asignar'}
          bordButtonStyle="outlined"
          customClassName="button"
          buttonIconTwo="plusCircle"
          disabled={loadingUserId !== undefined}
          onClick={handleClick(
            user?.id,
            isAssigned ? EQuoteResponsiblesActions.UNASSIGN : EQuoteResponsiblesActions.ASSIGN
          )}
        />
      </div>
    );
  };

  return (
    <div className="quoteResponsibles">
      <button className="button" onClick={handleOpen}>
        {!!userIdsSelected?.length && (
          <HoverReveal
            disabled={open}
            content={<div className="usersHoverReveal ">{userIdsSelected.map(renderUsers)}</div>}
          >
            <div className="avatarGroup">
              <div className="flex">
                {userIdsSelected?.slice(0, NUMBER_OF_USERS_TO_DISPLAY)?.map(renderAvatarGroup)}
              </div>
              {!!numberOfUsersDosentDisplayed && (
                <span className="usersCount">
                  {numberOfUsersDosentDisplayed ? `+${numberOfUsersDosentDisplayed}` : ''}
                </span>
              )}
            </div>
          </HoverReveal>
        )}
        {!userIdsSelected?.length && <>Sin responsables</>}
      </button>
      {open && (
        <div className="listContainer" style={style} ref={ref}>
          <BordSearchBar handleChange={setSearchText} placeholder="Busca por nombre o correo" />
          <div className="userListContainer">{users?.filter(user => search(user))?.map(renderUserItem)}</div>
        </div>
      )}
    </div>
  );
};
